import React from 'react';
import { DollarSign, FileText, ExternalLink } from 'lucide-react';
import { Expense } from '../../types/expenses';
import { format, parseISO } from 'date-fns';

interface ExpenseCardProps {
  expense: Expense;
}

export function ExpenseCard({ expense }: ExpenseCardProps) {
  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-6 shadow-soft dark:shadow-soft-dark hover:shadow-medium dark:hover:shadow-medium-dark transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-2">
            <DollarSign className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(expense.amount)}
              </span>
              {expense.action === 'SPENDING' && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                  Spending
                </span>
              )}
              {expense.action === 'PENALTY' && (
                <span className="text-xs px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
                  Penalty
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {expense.comment || 'No comment'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formatDate(expense.date || expense.createdAt)}
            </p>
            {expense.categoryName && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Category: {expense.categoryName}
              </div>
            )}
            {expense.woNumber && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                WO Number: {expense.woNumber}
              </div>
            )}
            {expense.files?.length > 0 && (
              <>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 mb-1">
                  Attachments ({expense.files.length}):
                </div>
                <div className="flex flex-wrap gap-2">
                  {expense.files.map((file) => (
                    <a
                      key={file.id}
                      href={file.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="truncate max-w-[150px]">{file.fileName}</span>
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}